/**
 * Prep Pipeline Service — single chokepoint for every preparation-request
 * status transition (CLAUDE.md §26.11, §28.2): the prep counterpart of
 * services/pipelineService.js, which does the same for client files.
 *
 * Every mutation that flips `preparation_requests.status` goes through this
 * module. Each transition writes the status (and its timestamp) FIRST and
 * the matching system note SECOND, through safePrepSystemNote, so a notes
 * outage can never block or undo a transition. A failed status write throws
 * to the caller; the view catches it, shows the error and stays on the page.
 *
 * The service knows nothing about React or navigation: it returns the
 * updated record and the view decides what follows — the return to the
 * dashboard with the card highlighted in its new column (§39) stays in
 * PrepRequestDetail.
 *
 * Notes, exactly as the view wrote them before step P6: only the draw
 * writes a system note ("Preparation started by ⟨name⟩.", §40). Completion,
 * reopen and revert write none.
 *
 * Transitions (CLAUDE.md §28.2):
 *   drawPrep           Inbox       → In Progress   set started_at = now()
 *   completePrep       In Progress → Completed     set completed_at = now()
 *   reopenPrep         Completed   → In Progress   clear completed_at
 *   revertPrepToInbox  In Progress → Inbox         clear started_at
 *
 * drawPrep is the prep DRAW (Inbox draw gate, §40). Unlike the file draw it
 * applies neither the queue-top nor the one-in-progress rule (§40.3) — prep
 * requests carry no concurrency constraint. Start New Round (§26.10) is not
 * here: it snapshots prior_rounds and deliberately stays on the page.
 */

import { updatePrepRequest } from '../api/prepRequestsApi';
import { createSystemNote, NOTE_BODY_MAX } from '../api/prepNotesApi';
import { clipText } from '../utils/prepAmendmentNotes';

/**
 * Append a system note, swallowing errors so a notes outage cannot block a
 * status transition. Notes are append-only (FAIS audit requirement) so we
 * never delete on failure.
 */
async function safePrepSystemNote(prepId, body) {
  try {
    await createSystemNote(prepId, body);
  } catch (e) {
    console.warn('[prepPipelineService] system note write failed:', body, e);
  }
}

// ───────────────────────────────────────────────────────────────────────
// Transitions
// Each writes status + timestamp, then (where one exists) the system note.
// ───────────────────────────────────────────────────────────────────────

/**
 * Inbox → In Progress: the prep draw ("Start preparation", §40).
 * Records WHO started the request and when — the prep counterpart of
 * "File drawn by ⟨name⟩. Work has commenced." — through the existing prep
 * notes API. `actor` is { email, id, firstName, role } as the view builds it.
 */
export async function drawPrep(prep, actor) {
  const updated = await updatePrepRequest(prep.id, {
    status: 'in_progress',
    startedAt: new Date().toISOString(),
  });
  await safePrepSystemNote(
    prep.id,
    clipText(`Preparation started by ${actor?.firstName || 'User'}.`, NOTE_BODY_MAX)
  );
  return updated;
}

/**
 * In Progress → Completed ("Mark complete"). The completion gate (§26.8)
 * and the unticked-tasks confirm (§28.3) are the view's; this is the write.
 * No system note (as before P6).
 */
export async function completePrep(prep /* actor unused */) {
  return updatePrepRequest(prep.id, {
    status: 'completed',
    completedAt: new Date().toISOString(),
  });
}

/**
 * Completed → In Progress ("Reopen"). No system note (as before P6).
 */
export async function reopenPrep(prep /* actor unused */) {
  return updatePrepRequest(prep.id, {
    status: 'in_progress',
    completedAt: null,
  });
}

/**
 * In Progress → Inbox ("Revert to Inbox"). The request re-seals for the New
 * Business Assistant (§40). No system note (as before P6).
 */
export async function revertPrepToInbox(prep /* actor unused */) {
  return updatePrepRequest(prep.id, {
    status: 'submitted',
    startedAt: null,
  });
}
